import {AppState, HoistService, InitContext, XH} from '@xh/hoist/core';
import {LogLevel, HubConnectionBuilder, HubConnection, HubConnectionState} from '@microsoft/signalr';
import {MessageTopicType, NotificationMessage, NotificationMessageCallbackFn, NotificationMessageSubscription} from '../data/MessageTypes';
import {isEmpty} from 'lodash';
import {RzxAuthModel} from '../../core/RzxAuthModel';
import {action, observable} from '@xh/hoist/mobx';

export type WsStatusType = 'OK' | 'RCV' | 'ERR';

export class MessageHub extends HoistService {
    private connection: HubConnection;
    private subscriptions: Map<MessageTopicType, NotificationMessageSubscription[]>;
    @observable accessor wsStatus: WsStatusType;

    override async initAsync(ctx: InitContext) {
        console.log('initializing MessageHub');
        this.subscriptions = new Map();

        const authModel = XH.authModel as RzxAuthModel;

        this.connection = new HubConnectionBuilder()
            .withUrl(`${XH.getConf('svcApiUrl')}/ws`, {accessTokenFactory: () => authModel.idToken})
            .configureLogging(LogLevel.Information)
            .build();

        this.registerListen('portfolio');
        this.registerListen('orders');
        this.registerListen('news');
        this.registerListen('watchlist');
        this.registerListen('price_quotes');
        this.registerListen('workflow_execution');
        this.registerListen('ai_chat');

        // Reconnect only while running - stop() on logout also fires onclose, and restarting then would
        // reopen the hub with the logged-out token. The appIsRunning reaction below reconnects on re-login.
        this.connection.onclose(() => {
            if (XH.appIsRunning) this.startWs(false);
        });

        await this.startWs(false);

        this.addReaction({
            track: () => XH.appIsRunning,
            run: isRunning => {
                if (!isRunning) {
                    console.warn('MessageHub: suspending WS svc updates');
                    this.connection.stop();
                } else if (this.connection.state === HubConnectionState.Disconnected) {
                    // App has (re)entered RUNNING (e.g. after a user logs back in as someone
                    // else following a logout) - the connection was torn down above and is not
                    // otherwise restarted, so WS updates would silently stay dead. Reconnect,
                    // picking up the current user's token via accessTokenFactory.
                    console.log('MessageHub: resuming WS svc updates');
                    this.startWs(false);
                }
            }
        });
    }

    private registerListen(topic: MessageTopicType) {
        this.connection.on(topic, message => {
            this.notify(message);
        });
    }

    private async startWs(isRestart: boolean) {
        if (XH.appState == AppState.SUSPENDED) return;

        try {
            await this.connection.start();
            console.log('MessageHub: signalR connected');
            this.updateWsStatus('OK');
            if (isRestart) {
                this.notifyReload();
            }
        } catch (err) {
            console.log(err);
            this.updateWsStatus('ERR');
            if (!XH.appIsRunning) {
                console.warn('MessageHub: suspending WS svc updates');
                return;
            }
            setTimeout(() => this.startWs(true), 5000);
        }
    }

    subscribe(topic: MessageTopicType, id: string, callbackFn: NotificationMessageCallbackFn) {
        const callbacks = this.subscriptions.get(topic);
        if (isEmpty(callbacks)) {
            this.subscriptions.set(topic, []);

            this.connection.invoke('Subscribe', topic);
        }

        console.log(`MessageHub: ${id} subscribing to [${topic}]`);
        this.subscriptions.get(topic)?.push({id, callbackFn});
    }

    unSubscribe(topic: MessageTopicType, id: string) {
        let subscriptions = this.subscriptions.get(topic);
        if (isEmpty(subscriptions)) return;

        subscriptions = subscriptions.filter(s => s.id != id);
        console.log(`MessageHub: ${id} un-subscribing from [${topic}]`);
        this.subscriptions.set(topic, subscriptions);

        if (isEmpty(subscriptions)) this.connection.invoke('UnSubscribe', topic);
    }

    private notify(message: NotificationMessage) {
        const subscriptions = this.subscriptions.get(message.envelope.topic);
        if (isEmpty(subscriptions)) return;

        this.updateWsStatus('RCV');
        subscriptions.forEach(subscription => {
            try {
                subscription.callbackFn?.(message);
            } catch (e) {
                console.error(e);
            }
        });
    }

    @action private updateWsStatus(status: WsStatusType) {
        if (status === this.wsStatus) return;

        this.wsStatus = status;
        if (status === 'RCV') {
            setTimeout(() => this.updateWsStatus('OK'), 300);
        }
    }

    private notifyReload() {
        const reloadMessage: NotificationMessage = {
            envelope: {eventType: 'RELOAD', id: -1, timestamp: null, topic: null},
            payload: null
        };
        try {
            for (const subscriptions of this.subscriptions.values()) {
                for (const subscription of subscriptions) {
                    subscription.callbackFn?.(reloadMessage);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }
}
